import { Fragment, useEffect, useMemo, useState } from 'react'
import {
  useGetIngredients,
  useDeleteIngredient,
  useGetIngredientCategories,
} from '../../hooks/backend/recipes'
import { Checkbox } from '../../lib/shadcn/checkbox'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../../lib/shadcn/select'
import { cn } from '../../lib/shadcn/utils'
import { RefreshCw, Plus, Search, X, Pencil, Copy, Trash2, Tags, ChevronDown, ChevronRight } from 'lucide-react'
import { toast } from '../../lib/shadcn/sonner'
import { useOnRestored, useUndoToast } from '../../hooks/useUndoToast'
import { formatUnitCost } from '../../utils/format'
import IngredientFormDialog, {
  type IngredientWithComponents,
} from './IngredientFormDialog'
import IngredientBulkEditDialog from './IngredientBulkEditDialog'
import { INGREDIENTS_CHANGED } from '../../utils/hubSearchEvents'
import IngredientCategoriesDialog, {
  type IngredientCategory,
} from './IngredientCategoriesDialog'

/** Category filter values besides a category id. */
const ALL = '__all'
const UNCATEGORIZED = '__none'

interface IngredientGroup {
  key: string
  name: string
  items: IngredientWithComponents[]
}

export default function IngredientsPanel() {
  const getIngredients = useGetIngredients()
  const deleteIngredient = useDeleteIngredient()
  const showUndo = useUndoToast()
  const getCategories = useGetIngredientCategories()

  const [filter, setFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState(ALL)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [categoriesOpen, setCategoriesOpen] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkOpen, setBulkOpen] = useState(false)
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<IngredientWithComponents | null>(null)
  const [duplicating, setDuplicating] = useState(false)

  const ingredients = (getIngredients.data as IngredientWithComponents[] | undefined) ?? []
  const categories = (getCategories.data as IngredientCategory[] | undefined) ?? []

  useEffect(() => {
    getIngredients.trigger()
    getCategories.trigger({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // A filter pointing at a category that no longer exists falls back to All.
  useEffect(() => {
    if (
      categoryFilter !== ALL &&
      categoryFilter !== UNCATEGORIZED &&
      getCategories.data &&
      !categories.some(c => c.id === categoryFilter)
    ) {
      setCategoryFilter(ALL)
    }
  }, [categories, categoryFilter, getCategories.data])

  const categoryIds = useMemo(() => new Set(categories.map(c => c.id)), [categories])
  const groupKey = (i: IngredientWithComponents) =>
    i.category_id && categoryIds.has(i.category_id) ? i.category_id : UNCATEGORIZED

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    return ingredients.filter(
      i =>
        (categoryFilter === ALL || groupKey(i) === categoryFilter) &&
        (!q ||
          i.name.toLowerCase().includes(q) ||
          (i.sub_ingredients ?? '').toLowerCase().includes(q)),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredients, filter, categoryFilter, categoryIds])

  // Groups in category order, Uncategorized last; empty groups are hidden.
  const groups = useMemo<IngredientGroup[]>(() => {
    const byKey = new Map<string, IngredientWithComponents[]>()
    for (const i of filtered) {
      const k = groupKey(i)
      const list = byKey.get(k) ?? []
      list.push(i)
      byKey.set(k, list)
    }
    const out: IngredientGroup[] = categories
      .filter(c => byKey.has(c.id))
      .map(c => ({ key: c.id, name: c.name, items: byKey.get(c.id)! }))
    const none = byKey.get(UNCATEGORIZED)
    if (none) out.push({ key: UNCATEGORIZED, name: 'Uncategorized', items: none })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, categories, categoryIds])

  // Only rows still shown count as selected, so a filter change can't
  // quietly include hidden ingredients in a bulk update.
  const selectedVisible = useMemo(
    () => filtered.filter(i => selected.has(i.id)),
    [filtered, selected],
  )
  const allVisibleSelected = filtered.length > 0 && selectedVisible.length === filtered.length

  const setChecked = (ids: string[], checked: boolean) =>
    setSelected(prev => {
      const next = new Set(prev)
      for (const id of ids) {
        if (checked) next.add(id)
        else next.delete(id)
      }
      return next
    })

  const checkState = (items: IngredientWithComponents[]) => {
    const n = items.filter(i => selected.has(i.id)).length
    return n === 0 ? false : n === items.length ? true : ('indeterminate' as const)
  }

  // With no categories set up yet, skip the group headings.
  const showGroupHeadings = categories.length > 0

  const uncategorizedCount = useMemo(
    () => ingredients.filter(i => groupKey(i) === UNCATEGORIZED).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ingredients, categoryIds],
  )

  const toggleGroup = (key: string) =>
    setCollapsed(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  const refreshCategories = () =>
    Promise.all([
      getCategories.trigger({}, { skipCache: true }),
      getIngredients.trigger(undefined, { skipCache: true }),
    ])
  const refreshAll = () => {
    void refreshCategories()
  }

  // An ingredient saved from the header search: refresh the list.
  useEffect(() => {
    const refresh = () => void refreshCategories()
    window.addEventListener(INGREDIENTS_CHANGED, refresh)
    return () => window.removeEventListener(INGREDIENTS_CHANGED, refresh)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // No "Are you sure?": the toast's Undo (and Settings > Recently deleted)
  // puts it back.
  const deleteNow = async (item: IngredientWithComponents) => {
    try {
      const result = (await deleteIngredient.trigger({ id: item.id })) as { trash_id?: number | null } | undefined
      showUndo(`Deleted ${item.name}`, result?.trash_id)
      await refreshCategories()
    } catch (error) {
      toast.error(
        `Failed to delete: ${error instanceof Error ? error.message : 'Unknown error'}`,
      )
    }
  }

  useOnRestored(['ingredient'], () => void refreshCategories())

  return (
    <div className="space-y-6">
      <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b">
          <h2 className="text-lg font-semibold">All Ingredients</h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Filter ingredients…"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                className="pl-8 h-8 w-52 text-sm"
              />
              {filter && (
                <button
                  onClick={() => setFilter('')}
                  aria-label="Clear filter"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="h-8 w-48 text-sm" aria-label="Filter by category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All categories</SelectItem>
                {categories.length > 0 ? <SelectSeparator /> : null}
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} ({c.ingredient_count})
                  </SelectItem>
                ))}
                <SelectSeparator />
                <SelectItem value={UNCATEGORIZED}>Uncategorized ({uncategorizedCount})</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={() => setCategoriesOpen(true)} className="gap-1.5">
              <Tags className="w-4 h-4" /> Categories
            </Button>
            <Button variant="outline" size="sm" onClick={refreshAll} className="gap-1.5">
              <RefreshCw className={cn('w-4 h-4', getIngredients.loading && 'animate-spin')} />
              Refresh
            </Button>
            <Button
              size="sm"
              onClick={() => { setEditing(null); setDuplicating(false); setFormOpen(true) }}
              className="gap-1.5"
            >
              <Plus className="w-4 h-4" /> Add Ingredient
            </Button>
          </div>
        </div>

        {selectedVisible.length > 0 ? (
          <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b bg-muted/40">
            <span className="text-sm font-medium">
              {selectedVisible.length} selected
            </span>
            <Button size="sm" onClick={() => setBulkOpen(true)} className="gap-1.5">
              <Pencil className="w-3.5 h-3.5" /> Bulk Edit
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              Clear selection
            </Button>
          </div>
        ) : null}

        {getIngredients.loading && ingredients.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-muted-foreground">
            Loading ingredients…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="sticky top-0 z-10 bg-card">
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={allVisibleSelected ? true : selectedVisible.length > 0 ? 'indeterminate' : false}
                      onCheckedChange={v => setChecked(filtered.map(i => i.id), !!v)}
                      aria-label="Select all shown ingredients"
                      disabled={filtered.length === 0}
                    />
                  </TableHead>
                  <TableHead className="text-xs">Name</TableHead>
                  <TableHead className="text-xs">Cost</TableHead>
                  <TableHead className="text-xs w-[180px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                      No ingredients found
                    </TableCell>
                  </TableRow>
                ) : (
                  groups.map(group => {
                    const isCollapsed = collapsed.has(group.key)
                    return (
                      <Fragment key={group.key}>
                        {showGroupHeadings ? (
                          <TableRow className="bg-muted/70 hover:bg-muted/70">
                            <TableCell colSpan={4} className="py-4">
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  checked={checkState(group.items)}
                                  onCheckedChange={v => setChecked(group.items.map(i => i.id), !!v)}
                                  aria-label={`Select all in ${group.name}`}
                                />
                                <button
                                  type="button"
                                  onClick={() => toggleGroup(group.key)}
                                  className="flex items-center gap-1.5 text-sm font-semibold"
                                  aria-expanded={!isCollapsed}
                                >
                                  {isCollapsed ? (
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                  )}
                                  <span className={cn(group.key === UNCATEGORIZED && 'italic text-muted-foreground')}>
                                    {group.name}
                                  </span>
                                  <span className="text-xs font-normal text-muted-foreground">
                                    {group.items.length}
                                  </span>
                                </button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : null}
                        {isCollapsed && showGroupHeadings ? null : group.items.map((ing, i) => (
                    <TableRow key={ing.id} className={cn(i % 2 === 1 && 'bg-muted/40', selected.has(ing.id) && 'bg-primary/5')}>
                      <TableCell className="py-4 w-10">
                        <Checkbox
                          checked={selected.has(ing.id)}
                          onCheckedChange={v => setChecked([ing.id], !!v)}
                          aria-label={`Select ${ing.name}`}
                        />
                      </TableCell>
                      <TableCell className="py-4">
                        <button
                          type="button"
                          onClick={() => { setEditing(ing); setDuplicating(false); setFormOpen(true) }}
                          className="text-left text-sm font-medium text-primary hover:underline"
                        >
                          {ing.name}
                        </button>
                      </TableCell>
                      <TableCell className="py-4">
                        {ing.cost != null ? (
                          <span className="text-sm tabular-nums">
                            {ing.cost_qty && ing.cost_qty !== 1 ? (
                              <>
                                ${(ing.cost * ing.cost_qty).toFixed(2)}
                                <span className="text-muted-foreground">
                                  {' '}/ {ing.cost_qty} {ing.cost_unit ?? ''}
                                </span>
                                <span className="ml-1.5 text-xs text-muted-foreground">
                                  ({formatUnitCost(ing.cost)}{ing.cost_unit ? `/${ing.cost_unit}` : ''})
                                </span>
                              </>
                            ) : (
                              <>
                                {formatUnitCost(ing.cost)}
                                {ing.cost_unit ? (
                                  <span className="text-muted-foreground"> / {ing.cost_unit}</span>
                                ) : null}
                              </>
                            )}
                          </span>
                        ) : (
                          <span className="text-xs italic text-muted-foreground/60">no cost</span>
                        )}
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5"
                            onClick={() => { setEditing(ing); setDuplicating(false); setFormOpen(true) }}
                          >
                            <Pencil className="w-3.5 h-3.5" /> Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            onClick={() => { setEditing(ing); setDuplicating(true); setFormOpen(true) }}
                            aria-label={`Duplicate ${ing.name}`}
                            title="Duplicate"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => void deleteNow(ing)}
                            aria-label={`Delete ${ing.name}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                        ))}
                      </Fragment>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <IngredientFormDialog
        ingredient={editing}
        duplicate={duplicating}
        existingNames={ingredients.map(i => i.name)}
        categories={categories}
        defaultCategoryId={
          categoryFilter !== ALL && categoryFilter !== UNCATEGORIZED ? categoryFilter : null
        }
        open={formOpen}
        onOpenChange={setFormOpen}
        onSaved={refreshAll}
      />

      <IngredientBulkEditDialog
        ingredients={selectedVisible}
        categories={categories}
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        onSaved={async () => {
          setSelected(new Set())
          await refreshCategories()
        }}
      />

      <IngredientCategoriesDialog
        categories={categories}
        open={categoriesOpen}
        onOpenChange={setCategoriesOpen}
        onChanged={refreshCategories}
      />

    </div>
  )
}
